import { CompanyLimitsCard } from '@/components/CompanyLimitsCard';
import { ReactNode, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import clsx from 'clsx';
import { Topbar } from '@/components/Topbar';
import { Button } from '@/components/Button';
import { Modal } from '@/components/Modal';
import { Input } from '@/components/Input';
import { Select } from '@/components/Select';
import { FormField } from '@/components/FormField';
import { StatusPill } from '@/components/StatusPill';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { LogoUpload } from '@/components/LogoUpload';
import {
  Activity, Bot, Briefcase, Building2, CalendarCheck, CalendarClock, CalendarDays, Clock, CreditCard,
  Globe, LayoutGrid, Megaphone, MessageSquare, MoreVertical, Pause, Pencil, Play, Repeat, Sparkles,
  Target, UserRound, Users,
} from 'lucide-react';
import { companiesService, Company, CompanyInsights, CustomLimits } from '@/services/companies.service';
import { KpiCard } from '@/components/insights/KpiCard';
import { ActivityChart } from '@/components/insights/ActivityChart';
import { UsageBar } from '@/components/insights/UsageBar';
import { HealthCard, healthMeta, relative } from '@/components/insights/HealthGauge';
import { plansService, Plan, PlanLimits } from '@/services/plans.service';
import { pricingService, PricingCatalog } from '@/services/pricing.service';
import { subscriptionsService, Subscription } from '@/services/subscriptions.service';
import { MODULE_REGISTRY, DEFAULT_MODULE_KEYS } from '@/config/modules';
import { INDUSTRY_OPTIONS } from '@/config/industries';
import { CompanyIndustryCard, LinkStateChip } from '@/components/industry/CompanyIndustryCard';
import { CompanyBlueprintCard } from '@/components/industry/CompanyBlueprintCard';
import { CompanyLocationCard } from '@/components/location/LocationFields';
import { industryIcon } from '@/pages/industry-configs/industry-meta';
import toast from 'react-hot-toast';

const isExplicit = (key: string) => MODULE_REGISTRY.some((m) => m.key === key && m.explicit);

type SeatKey =
  | 'whatsappAccounts' | 'facebookAccounts' | 'googleAdsAccounts'
  | 'tiktokAccounts'   | 'emailAccounts'
  | 'users'            | 'botFlows';

const SEAT_FIELDS: {
  key: SeatKey;
  label: string;
  priceField: keyof PricingCatalog;
  planField?: keyof PlanLimits;
}[] = [
  { key: 'whatsappAccounts',  label: 'WhatsApp accounts',  priceField: 'pricePerWhatsappAccount',  planField: 'maxWhatsappAccounts' },
  { key: 'facebookAccounts',  label: 'Facebook accounts',  priceField: 'pricePerFacebookAccount',  planField: 'maxFacebookAccounts' },
  { key: 'googleAdsAccounts', label: 'Google Ads accounts',priceField: 'pricePerGoogleAdsAccount', planField: 'maxGoogleAdsAccounts' },
  { key: 'tiktokAccounts',    label: 'TikTok accounts',    priceField: 'pricePerTiktokAccount',    planField: 'maxTiktokAccounts' },
  { key: 'emailAccounts',     label: 'Email accounts',     priceField: 'pricePerEmailAccount' },
  { key: 'users',             label: 'Users / seats',      priceField: 'pricePerUser',             planField: 'maxUsers' },
  { key: 'botFlows',          label: 'Bot flows',          priceField: 'pricePerBotFlow',          planField: 'maxBotFlows' },
];

type DetailTab = 'overview' | 'details' | 'modules' | 'subscription';

function MenuItem({ icon, label, onClick, danger }: { icon: ReactNode; label: string; onClick: () => void; danger?: boolean }) {
  return (
    <button
      onClick={onClick}
      className={clsx(
        'flex w-full items-center gap-2.5 px-3.5 py-2 text-left text-[13px] transition-colors',
        danger ? 'text-danger hover:bg-danger/10' : 'text-text-primary hover:bg-surface-alt',
      )}
    >
      <span className={danger ? 'text-danger' : 'text-text-muted'}>{icon}</span>
      {label}
    </button>
  );
}

/**
 * One vital sign on the account record. Deliberately borderless: the previous
 * bordered/segmented strip sat next to the Edit button and read as a row of
 * buttons. Value first, quiet label under it — data, not controls.
 */
function HeroStat({ icon, label, value, warn }: { icon: ReactNode; label: string; value: string; warn?: boolean }) {
  return (
    <div className="min-w-0">
      <div className={clsx(
        'truncate font-display text-[17px] font-semibold leading-6 tabular-nums',
        warn ? 'text-warning' : 'text-text-primary',
      )}>
        {value}
      </div>
      <div className="mt-0.5 inline-flex items-center gap-1 text-[11px] leading-4 text-text-muted">
        <span className="opacity-70">{icon}</span>{label}
      </div>
    </div>
  );
}

function InfoRow({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-4 border-b border-border/60 py-2">
      <dt className="shrink-0 text-[11px] font-medium uppercase tracking-[0.06em] text-text-muted">{label}</dt>
      <dd className="min-w-0 truncate text-right text-[13px] text-text-primary">{value}</dd>
    </div>
  );
}

export default function CompanyDetail() {
  const { uid } = useParams<{ uid: string }>();
  const [c, setC] = useState<Company | null>(null);
  const [loadError, setLoadError] = useState(false);
  const [tab, setTab] = useState<DetailTab>('overview');
  const [ins, setIns] = useState<CompanyInsights | null>(null);
  // Module toggles draft. null enabledModules (legacy) = every module on.
  const [modulesDraft, setModulesDraft] = useState<Set<string>>(new Set(DEFAULT_MODULE_KEYS));
  const [savingModules, setSavingModules] = useState(false);
  const [plans, setPlans] = useState<Plan[]>([]);
  const [subs, setSubs] = useState<Subscription[]>([]);
  const [catalog, setCatalog] = useState<PricingCatalog | null>(null);
  const [planModal, setPlanModal] = useState(false);
  const [extendModal, setExtendModal] = useState(false);
  const [seatsModal, setSeatsModal] = useState(false);
  const [confirmSuspend, setConfirmSuspend] = useState(false);
  const [newPlanId, setNewPlanId] = useState(''); const [planNote, setPlanNote] = useState('');
  const [newExpiry, setNewExpiry] = useState(''); const [expiryNote, setExpiryNote] = useState('');
  const [seatsDraft, setSeatsDraft] = useState<CustomLimits>({});
  const [savingSeats, setSavingSeats] = useState(false);
  const [actionsOpen, setActionsOpen] = useState(false);
  const [editModal, setEditModal] = useState(false);
  const [editName, setEditName] = useState('');
  const [editDomain, setEditDomain] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);

  const refresh = () => { setLoadError(false); return Promise.all([
    companiesService.get(uid!).then((company) => {
      setC(company);
      setModulesDraft(new Set(
        company.enabledModules == null ? DEFAULT_MODULE_KEYS : company.enabledModules,
      ));
    }),
    subscriptionsService.byCompany(uid!).then(setSubs),
    // Usage is read-only and slow-ish (many aggregates) — never let it block the config tabs.
    companiesService.insights(uid!).then(setIns).catch(() => setIns(null)),
  ]).catch(() => setLoadError(true)); };

  const toggleModule = (key: string) =>
    setModulesDraft((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });

  const saveModules = async () => {
    if (!c) return;
    if (modulesDraft.size === 0 && !window.confirm(
      'No modules selected — this company will only see Settings. Continue?',
    )) return;
    setSavingModules(true);
    try {
      await companiesService.update(c.companyUid, { enabledModules: Array.from(modulesDraft) });
      toast.success('Modules updated — the company sidebar reflects this on their next page load');
      refresh();
    } catch { /* interceptor toasts */ } finally { setSavingModules(false); }
  };

  useEffect(() => {
    plansService.list().then(setPlans).catch(() => setLoadError(true));
    pricingService.get().then(setCatalog).catch(() => setLoadError(true));
    refresh();
    // eslint-disable-next-line
  }, [uid]);

  // Effective seat allocation = override > plan default > 0.
  const effectivePlan = useMemo(() => plans.find((p) => p._id === c?.planId), [plans, c]);
  const effective = (key: SeatKey): number => {
    const override = c?.customLimits?.[key];
    if (typeof override === 'number') return override;
    const planMax = SEAT_FIELDS.find((f) => f.key === key)?.planField;
    return (planMax ? (effectivePlan?.limits[planMax] as number | undefined) : undefined) ?? 0;
  };
  const unitPrice = (key: SeatKey): number => {
    if (!catalog) return 0;
    const f = SEAT_FIELDS.find((x) => x.key === key)!;
    return (catalog[f.priceField] as number | undefined) ?? 0;
  };

  const totalMonthly = useMemo(() => {
    if (!catalog) return 0;
    const subtotal = SEAT_FIELDS.reduce((s, f) => s + effective(f.key) * unitPrice(f.key), 0);
    return Math.round((catalog.baseMonthlyFee + subtotal) * 100) / 100;
  }, [catalog, c, plans]); // eslint-disable-line

  const toggleSuspend = async () => {
    if (!c) return;
    const next = c.status === 'suspended' ? 'active' : 'suspended';
    await companiesService.update(c.companyUid, { status: next });
    toast.success(next === 'suspended' ? 'Suspended' : 'Reactivated'); setConfirmSuspend(false); refresh();
  };

  const submitChangePlan = async () => {
    if (!c) return;
    await subscriptionsService.changePlan(c.companyUid, newPlanId, planNote);
    toast.success('Plan changed'); setPlanModal(false); refresh();
  };

  const submitExtend = async () => {
    if (!c) return;
    await subscriptionsService.extendExpiry(c.companyUid, new Date(newExpiry).toISOString(), expiryNote);
    toast.success('Expiry extended'); setExtendModal(false); refresh();
  };

  const setLogo = async (url: string | undefined) => {
    if (!c) return;
    await companiesService.update(c.companyUid, { logoUrl: url ?? '' });
    toast.success(url ? 'Logo updated' : 'Logo removed');
    refresh();
  };

  const openEdit = () => {
    if (!c) return;
    setEditName(c.name); setEditDomain(c.domain ?? '');
    setEditModal(true);
  };

  const submitEdit = async () => {
    if (!c || !editName.trim()) return;
    setSavingEdit(true);
    try {
      // Empty domain intentionally clears it (backend patches any non-undefined value).
      await companiesService.update(c.companyUid, { name: editName.trim(), domain: editDomain.trim() });
      toast.success('Company updated');
      setEditModal(false);
      refresh();
    } catch { /* interceptor toasts */ } finally { setSavingEdit(false); }
  };

  const openSeats = () => {
    if (!c) return;
    // Pre-fill with current effective values so the user sees what they're editing.
    const draft: CustomLimits = {};
    SEAT_FIELDS.forEach((f) => { draft[f.key] = effective(f.key); });
    setSeatsDraft(draft);
    setSeatsModal(true);
  };

  const submitSeats = async () => {
    if (!c) return;
    setSavingSeats(true);
    try {
      // Merge with any existing customLimits the schema has but our card
      // doesn't expose (contacts, messagesPerMonth, etc.), so we don't lose them.
      const next: CustomLimits = { ...(c.customLimits ?? {}) };
      SEAT_FIELDS.forEach((f) => {
        const v = seatsDraft[f.key];
        if (typeof v === 'number' && v >= 0) next[f.key] = v;
      });
      await companiesService.update(c.companyUid, {
        usesCustomPricing: true,
        customLimits: next,
      });
      toast.success('Seats updated');
      setSeatsModal(false);
      refresh();
    } catch { /* axios interceptor toasts */ } finally { setSavingSeats(false); }
  };

  if (!c) return <><Topbar title="Company" backTo="/companies" /><div className="p-6 text-text-muted">{loadError ? <div role="alert">Could not load this company. <Button variant="secondary" size="sm" onClick={refresh}>Retry</Button></div> : 'Loading…'}</div></>;

  const currency = catalog?.currency ?? 'USD';
  const industryLabel = c.industry
    ? (INDUSTRY_OPTIONS.find((i) => i.value === c.industry)?.label ?? c.industry)
    : null;
  const expiringSoon = new Date(c.expiresAt).getTime() < Date.now() + 14 * 864e5;

  return (
    <>
      <Topbar
        title={c.name}
        breadcrumb={`Customers / Companies / ${c.name}`}
        backTo="/companies"
        right={
          <div className="relative">
            <button
              onClick={() => setActionsOpen((v) => !v)}
              className="flex h-9 w-9 items-center justify-center rounded-lg border border-border text-text-muted transition-colors hover:bg-surface-alt hover:text-text-primary"
              aria-label="Company actions"
            >
              <MoreVertical size={16} />
            </button>
            {actionsOpen && (
              <>
                {/* click-away layer */}
                <div className="fixed inset-0 z-40" onClick={() => setActionsOpen(false)} />
                <div className="absolute right-0 top-11 z-50 w-52 overflow-hidden rounded-xl border border-border bg-surface py-1 shadow-xl shadow-black/10">
                  <MenuItem
                    icon={<CalendarClock size={14} />}
                    label="Extend expiry"
                    onClick={() => { setActionsOpen(false); setNewExpiry(new Date(c.expiresAt).toISOString().slice(0, 10)); setExtendModal(true); }}
                  />
                  <MenuItem
                    icon={<Repeat size={14} />}
                    label="Change plan"
                    onClick={() => { setActionsOpen(false); setNewPlanId(c.planId); setPlanModal(true); }}
                  />
                  <div className="my-1 h-px bg-border" />
                  <MenuItem
                    icon={c.status === 'suspended' ? <Play size={14} /> : <Pause size={14} />}
                    label={c.status === 'suspended' ? 'Reactivate company' : 'Suspend company'}
                    danger={c.status !== 'suspended'}
                    onClick={() => { setActionsOpen(false); setConfirmSuspend(true); }}
                  />
                </div>
              </>
            )}
          </div>
        }
      />
      <div className="p-6 space-y-4">
        {loadError && <div role="alert" className="rounded-xl border border-warning/20 bg-warning-soft p-3 text-sm text-warning">Some company details could not be loaded. Refresh the page before making changes.</div>}
        {c.setupStatus && c.setupStatus !== 'ready' && <div role="status" className="rounded-xl border border-warning/20 bg-warning-soft p-4 text-sm text-warning"><strong>Industry setup {c.setupStatus === 'pending' ? 'is pending' : 'needs attention'}.</strong><p className="mt-1 text-xs">{c.setupWarnings?.[0] ?? 'Review and apply the industry blueprint below to finish setup.'}</p><Button size="sm" variant="secondary" className="mt-2" onClick={() => setTab('details')}>Review setup</Button></div>}
        {/* ── Hero ─────────────────────────────────────────────────────── */}
        <div className="overflow-hidden rounded-xl border border-border bg-surface">
          <div className="h-1 bg-gradient-to-r from-primary via-primary/40 to-transparent" />
          <div className="flex flex-wrap items-start gap-4 px-5 pb-4 pt-4">
            <LogoUpload value={c.logoUrl} name={c.name} onChange={setLogo} size={76} />
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2.5">
                <h2 className="truncate text-lg font-semibold text-text-primary">{c.name}</h2>
                <StatusPill status={c.status} />
                {ins && (
                  <span className={clsx('inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold', healthMeta(ins.health.label).cls)}>
                    <Activity size={11} /> {healthMeta(ins.health.label).text}
                  </span>
                )}
                {industryLabel && (() => {
                  const HeroIndustryIcon = industryIcon(c.industry!);
                  return (
                    <button
                      type="button"
                      onClick={() => setTab('details')}
                      className="inline-flex items-center gap-1 rounded-full bg-surface-alt px-2 py-0.5 text-xs font-medium text-text-secondary transition-colors hover:text-text-primary"
                      title="Industry — open Company Details"
                    >
                      <HeroIndustryIcon size={11} /> {industryLabel}
                    </button>
                  );
                })()}
                {c.industryLink && (c.industryLink.state === 'mismatch' || c.industryLink.state === 'crm_only') && (
                  <button type="button" onClick={() => setTab('details')}>
                    <LinkStateChip state={c.industryLink.state} />
                  </button>
                )}
              </div>
              <div className="mt-1.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-text-muted">
                <span className="font-mono uppercase tracking-wide">{c.slug}</span>
                {c.domain && <span className="inline-flex items-center gap-1"><Globe size={12} /> {c.domain}</span>}
                <span className="inline-flex items-center gap-1"><CalendarDays size={12} /> Client since {new Date(c.createdAt).toLocaleDateString()}</span>
              </div>
            </div>
            <button
              onClick={openEdit}
              className="inline-flex shrink-0 items-center gap-1.5 self-start rounded-lg px-2.5 py-1.5 text-[13px] font-medium text-text-muted transition-colors hover:bg-surface-alt hover:text-text-primary"
            >
              <Pencil size={13} /> Edit
            </button>
          </div>

          {/* Vital signs — money and access first, freshness last. */}
          <div className="grid grid-cols-2 gap-x-6 gap-y-3 border-t border-border px-5 py-3.5 sm:grid-cols-3 lg:grid-cols-5">
            <HeroStat icon={<Users size={11} />} label="Users" value={String(c.userCount ?? '—')} />
            <HeroStat icon={<CreditCard size={11} />} label="Plan" value={effectivePlan?.displayName ?? '—'} />
            <HeroStat
              icon={<CalendarDays size={11} />}
              label={expiringSoon ? 'Renews soon' : 'Renews'}
              value={new Date(c.expiresAt).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })}
              warn={expiringSoon}
            />
            <HeroStat
              icon={<LayoutGrid size={11} />}
              label="Modules enabled"
              value={c.enabledModules == null ? `All ${MODULE_REGISTRY.length}` : `${c.enabledModules.length} of ${MODULE_REGISTRY.length}`}
            />
            <HeroStat
              icon={<Clock size={11} />}
              label="Last active"
              value={ins ? relative(ins.activity.lastActivityAt) : '—'}
              warn={!!ins && (!ins.activity.lastActivityAt || Date.now() - new Date(ins.activity.lastActivityAt).getTime() > 14 * 864e5)}
            />
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 border-b border-border">
          {([
            { key: 'overview', label: 'Overview', icon: Activity },
            { key: 'details', label: 'Company Details', icon: Building2 },
            { key: 'modules', label: 'Modules', icon: LayoutGrid },
            { key: 'subscription', label: 'Subscription & Billing', icon: CreditCard },
          ] as { key: DetailTab; label: string; icon: typeof Building2 }[]).map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              onClick={() => setTab(key)}
              className={clsx(
                'inline-flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors',
                tab === key
                  ? 'border-primary text-primary'
                  : 'border-transparent text-text-muted hover:text-text-primary',
              )}
            >
              <Icon size={14} />
              {label}
              {key === 'modules' && c.enabledModules != null && (
                <span className="rounded-full bg-primary/15 px-1.5 py-0.5 text-2xs font-bold text-primary">
                  {c.enabledModules.length}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* ── Tab: Overview ────────────────────────────────────────────── */}
        {tab === 'overview' && (
          !ins ? (
            <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="h-[92px] rounded-xl border border-border bg-surface p-4">
                  <div className="shimmer h-3 w-16 rounded bg-border/60" />
                  <div className="shimmer mt-4 h-6 w-20 rounded bg-border/60" />
                </div>
              ))}
            </div>
          ) : (() => {
            const msgs30 = ins.series.reduce((a, r) => a + r.inbound + r.outbound, 0);
            const inbound30 = ins.series.reduce((a, r) => a + r.inbound, 0);
            const leads30 = ins.series.reduce((a, r) => a + r.leads, 0);
            return (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
                  <KpiCard icon={<UserRound size={14} />} label="Contacts" value={ins.counts.contacts} delay={0} />
                  <KpiCard icon={<Target size={14} />} label="Leads" value={ins.counts.leads} hint={`${leads30} new in 30d`} delay={40} />
                  <KpiCard icon={<Briefcase size={14} />} label="Deals" value={ins.counts.deals} delay={80} />
                  <KpiCard icon={<CalendarCheck size={14} />} label="Bookings" value={ins.counts.bookings} delay={120} />
                  <KpiCard
                    icon={<MessageSquare size={14} />} label="Messages · 30d" value={msgs30}
                    hint={msgs30 ? `${inbound30} received` : 'No WhatsApp traffic'} tone={msgs30 ? 'default' : 'warn'} delay={160}
                  />
                  <KpiCard
                    icon={<Sparkles size={14} />} label="AI spend · 30d" value={ins.ai.costUsd30d}
                    format={(n) => `$${n.toFixed(2)}`} hint={`${ins.ai.generations30d} studio generation${ins.ai.generations30d === 1 ? '' : 's'}`} delay={200}
                  />
                </div>

                <div className="grid items-start gap-4 xl:grid-cols-5">
                  <div className="space-y-4 xl:col-span-3">
                    <ActivityChart rows={ins.series} />
                  <div className="fade-up rounded-xl border border-border bg-surface p-5" style={{ animationDelay: '260ms' }}>
                    <h3 className="text-sm font-semibold text-text-primary">Seats & automation</h3>
                    <p className="mb-4 mt-0.5 text-2xs text-text-muted">What the company has built against what it pays for.</p>
                    <div className="space-y-4">
                      <UsageBar icon={<Users size={13} />} label="Users" used={ins.counts.users} limit={effective('users')} delay={280} />
                      <UsageBar icon={<Bot size={13} />} label="Bot flows" used={ins.counts.botFlows} limit={effective('botFlows')} delay={320} />
                      <UsageBar icon={<Megaphone size={13} />} label="Broadcast campaigns" used={ins.counts.broadcasts} delay={360} />
                      <UsageBar icon={<Sparkles size={13} />} label="AI generations · 30d" used={ins.ai.generations30d} delay={400} />
                    </div>
                  </div>
                  </div>
                  <div className="space-y-4 xl:col-span-2">
                  <HealthCard
                    score={ins.health.score} label={ins.health.label} reasons={ins.health.reasons}
                    lastActivityAt={ins.activity.lastActivityAt} lastLoginAt={ins.activity.lastLoginAt}
                    activeUsers={ins.activity.activeUsers30d} totalUsers={ins.counts.users} delay={160}
                  />
                  <div className="fade-up rounded-xl border border-border bg-surface p-5" style={{ animationDelay: '220ms' }}>
                    <h3 className="text-sm font-semibold text-text-primary">Connected channels</h3>
                    <p className="mb-4 mt-0.5 text-2xs text-text-muted">Accounts linked vs seats in the plan.</p>
                    <div className="space-y-4">
                      <UsageBar icon={<MessageSquare size={13} />} label="WhatsApp numbers" used={ins.channels.whatsapp.connected} limit={effective('whatsappAccounts')} delay={240} />
                      <UsageBar icon={<Megaphone size={13} />} label="Meta ad accounts" used={ins.channels.meta.connected} limit={effective('facebookAccounts')} delay={280} />
                      <UsageBar icon={<Megaphone size={13} />} label="TikTok ad accounts" used={ins.channels.tiktok.connected} limit={effective('tiktokAccounts')} delay={320} />
                      <UsageBar icon={<Globe size={13} />} label="Google Ads accounts" used={ins.channels.google.connected} limit={effective('googleAdsAccounts')} delay={360} />
                    </div>
                  </div>
                  </div>
                </div>
              </div>
            );
          })()
        )}

        {/* ── Tab: Company Details ─────────────────────────────────────── */}
        {tab === 'details' && (
        <div className="grid gap-4 xl:grid-cols-3">
          <div className="rounded-xl border border-border bg-surface p-5 xl:col-span-2">
            <h3 className="mb-2 inline-flex items-center gap-2 text-sm font-semibold">
              <Building2 size={15} className="text-primary" /> Company information
            </h3>
            <dl className="grid grid-cols-1 gap-x-10 sm:grid-cols-2">
              <InfoRow label="Company ID" value={<span className="font-mono text-xs">{c.companyUid}</span>} />
              <InfoRow label="Code / slug" value={<span className="font-mono text-xs uppercase">{c.slug}</span>} />
              <InfoRow label="Domain" value={c.domain || '—'} />
              <InfoRow label="Created" value={new Date(c.createdAt).toLocaleDateString()} />
              <InfoRow label="Plan" value={effectivePlan?.displayName ?? c.planId.slice(-6)} />
              <InfoRow label="Custom pricing" value={c.usesCustomPricing ? 'Yes' : 'No'} />
              <InfoRow label="Status" value={<StatusPill status={c.status} />} />
              <InfoRow label="Expires" value={
                <span className={expiringSoon ? 'font-semibold text-warning' : undefined}>
                  {new Date(c.expiresAt).toLocaleDateString()}
                </span>
              } />
              <InfoRow
                label="Platform owner"
                value={
                  <button
                    type="button"
                    onClick={async () => {
                      const next = !c.isPlatformOwner;
                      if (!window.confirm(next
                        ? `Mark ${c.name} as the platform owner? This unlocks the Products module (linking your own apps to this company's WhatsApp numbers). Only Mi Coders should have this.`
                        : `Remove platform-owner status from ${c.name}? The Products module disappears from their CRM.`)) return;
                      await companiesService.update(c.companyUid, { isPlatformOwner: next });
                      toast.success(next ? 'Marked as platform owner' : 'Platform-owner status removed');
                      refresh();
                    }}
                    className={clsx(
                      'inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-semibold transition-colors',
                      c.isPlatformOwner ? 'bg-primary/15 text-primary hover:bg-primary/25' : 'bg-surface-alt text-text-muted hover:text-text-primary',
                    )}
                    title="Only the operator's own company (Mi Coders). Unlocks the Products module."
                  >
                    <Building2 size={11} /> {c.isPlatformOwner ? 'Yes — our own company' : 'No'}
                  </button>
                }
              />
            </dl>
            <p className="mt-3 text-2xs text-text-muted">
              Name, domain and logo are edited from the <em>Edit</em> button in the header.
            </p>
          </div>

          <CompanyIndustryCard company={c} onChanged={refresh} />
          <CompanyBlueprintCard companyUid={c.companyUid} industry={c.industry} />
          <CompanyLocationCard company={c} onChanged={refresh} />
        </div>
        )}

        {/* ── Tab: Modules ─────────────────────────────────────────────── */}
        {tab === 'modules' && (
        <div className="bg-surface border border-border rounded-lg p-5">
          <div className="mb-4 flex items-start justify-between gap-4">
            <div>
              <h3 className="text-sm font-semibold">SlightorCRM modules</h3>
              <p className="text-xs text-text-muted mt-0.5 max-w-xl">
                Only the enabled modules appear in this company's sidebar. A client who only needs
                bookings and WhatsApp sees exactly those — nothing else. Settings is always available.
              </p>
            </div>
            <div className="flex shrink-0 gap-2">
              <Button variant="secondary" onClick={() => setModulesDraft((prev) => new Set([...DEFAULT_MODULE_KEYS, ...Array.from(prev).filter(isExplicit)]))}>Enable all</Button>
              <Button variant="secondary" onClick={() => setModulesDraft(new Set())}>Disable all</Button>
              <Button onClick={saveModules} disabled={savingModules}>{savingModules ? 'Saving…' : 'Save modules'}</Button>
            </div>
          </div>

          {(['Core', 'WhatsApp', 'Marketing', 'Industry', 'AI'] as const).map((group) => (
            <div key={group} className="mb-4">
              <div className="mb-2 text-2xs font-semibold uppercase tracking-wider text-text-muted">{group}</div>
              <div className="grid grid-cols-1 gap-2 md:grid-cols-2 xl:grid-cols-3">
                {MODULE_REGISTRY.filter((m) => m.group === group).map((m) => {
                  const on = modulesDraft.has(m.key);
                  const Icon = m.icon;
                  return (
                    <button
                      key={m.key}
                      type="button"
                      aria-pressed={on}
                      onClick={() => toggleModule(m.key)}
                      className={clsx(
                        'flex items-center gap-2.5 rounded-lg border px-2.5 py-2 text-left transition-colors',
                        on ? 'border-primary/40 bg-primary/5' : 'border-border bg-bg/30 opacity-60 hover:opacity-100',
                      )}
                    >
                      <span className={clsx(
                        'flex h-8 w-8 shrink-0 items-center justify-center rounded-lg',
                        on ? 'bg-primary/15 text-primary' : 'bg-surface-alt text-text-muted',
                      )}>
                        <Icon size={15} />
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="flex items-center gap-1.5 truncate text-[13px] font-semibold leading-5 text-text-primary">{m.label}{m.explicit && <span className="rounded-full bg-warning/15 px-1.5 text-[9px] font-bold uppercase tracking-wider text-warning">Assign only</span>}</span>
                        <span className="block truncate text-[11px] leading-4 text-text-muted" title={m.description}>{m.description}</span>
                      </span>
                      {/* Toggle */}
                      <span className={clsx(
                        'relative inline-flex h-4 w-7 shrink-0 rounded-full transition-colors',
                        on ? 'bg-primary' : 'bg-border',
                      )}>
                        <span className={clsx(
                          'absolute top-0.5 h-3 w-3 rounded-full bg-white shadow transition-all',
                          on ? 'left-[14px]' : 'left-0.5',
                        )} />
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          ))}

          <p className="text-2xs text-text-muted italic">
            {c.enabledModules == null
              ? 'This company currently has NO restriction saved — it sees every module (legacy default). Saving stores an explicit list.'
              : `${c.enabledModules.length} of ${MODULE_REGISTRY.length} modules enabled.`}
          </p>
        </div>
        )}

        {/* ── Tab: Subscription & Billing ──────────────────────────────── */}
        {tab === 'subscription' && (
        <>
        {/* Seats & Modules */}
        <div className="bg-surface border border-border rounded-xl p-5">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-sm font-semibold">Module seats</h3>
              <p className="text-xs text-text-muted mt-0.5">
                Per-module allocation for this company. Edit the count to grant more or fewer seats.
                Prices come from the global Pricing Catalog.
              </p>
            </div>
            <Button variant="secondary" onClick={openSeats}>Edit seats</Button>
          </div>

          <div className="overflow-hidden rounded-md border border-border">
            <table className="w-full text-sm">
              <thead className="bg-bg/40 text-text-muted">
                <tr>
                  <th className="px-3 py-1.5 text-left text-[10px] font-semibold uppercase tracking-[0.08em]">Module</th>
                  <th className="px-3 py-1.5 text-right text-[10px] font-semibold uppercase tracking-[0.08em]">Seats</th>
                  <th className="px-3 py-1.5 text-right text-[10px] font-semibold uppercase tracking-[0.08em]">Unit / mo</th>
                  <th className="px-3 py-1.5 text-right text-[10px] font-semibold uppercase tracking-[0.08em]">Subtotal</th>
                  <th className="px-3 py-1.5 text-left text-[10px] font-semibold uppercase tracking-[0.08em]">Source</th>
                </tr>
              </thead>
              <tbody>
                {SEAT_FIELDS.map((f) => {
                  const seats = effective(f.key);
                  const price = unitPrice(f.key);
                  const isOverride = typeof c.customLimits?.[f.key] === 'number';
                  return (
                    <tr key={f.key} className="border-t border-border text-[13px]">
                      <td className="px-3 py-2 font-medium text-text-primary">{f.label}</td>
                      <td className="px-3 py-2 text-right tabular-nums font-semibold">{seats}</td>
                      <td className="px-3 py-2 text-right tabular-nums text-text-muted">
                        {currency} {price.toFixed(2)}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums">
                        {currency} {(seats * price).toFixed(2)}
                      </td>
                      <td className="px-3 py-2">
                        {isOverride ? (
                          <span className="inline-flex items-center rounded-full bg-primary/10 px-1.5 py-px text-[10px] font-semibold uppercase tracking-[0.06em] text-primary">
                            Override
                          </span>
                        ) : (
                          <span className="text-[11px] text-text-muted">Plan default</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="border-t border-border bg-bg/40 text-[13px]">
                  <td className="px-3 py-2 text-text-muted text-[11px] font-semibold">Base monthly fee</td>
                  <td colSpan={2} />
                  <td className="px-3 py-2 text-right tabular-nums">
                    {currency} {(catalog?.baseMonthlyFee ?? 0).toFixed(2)}
                  </td>
                  <td />
                </tr>
                <tr className="border-t border-border bg-bg/40 text-[13px] font-bold">
                  <td className="px-3 py-2">Total / month</td>
                  <td colSpan={2} />
                  <td className="px-3 py-2 text-right tabular-nums">
                    {currency} {totalMonthly.toFixed(2)}
                  </td>
                  <td />
                </tr>
              </tfoot>
            </table>
          </div>

          {!c.usesCustomPricing && (
            <p className="mt-3 text-2xs text-text-muted italic">
              Custom pricing is OFF — totals shown are informational. Saving any seat change will turn it on.
            </p>
          )}
        </div>

        <CompanyLimitsCard key={c.companyUid} company={c} plan={effectivePlan} onSaved={refresh} />

        {/* Subscription history */}
        <div className="bg-surface border border-border rounded-xl p-5">
          <h3 className="text-sm font-semibold mb-3">Subscription history</h3>
          <ul className="space-y-2 text-[13px]">
            {subs.map(s => (
              <li key={s._id} className="flex justify-between border-b border-border pb-2">
                <span><strong>{s.action}</strong> · {typeof s.planId === 'object' ? s.planId.displayName : s.planId.slice(-6)}{s.note ? ` — ${s.note}` : ''}</span>
                <span className="text-text-muted">{new Date(s.createdAt).toLocaleString()}</span>
              </li>
            ))}
          </ul>
        </div>
        </>
        )}
      </div>

      <Modal open={editModal} onClose={() => setEditModal(false)} title="Edit company">
        <div className="mb-4 flex items-center gap-4">
          <LogoUpload value={c.logoUrl} name={c.name} onChange={setLogo} size={64} />
          <p className="text-xs text-text-muted">
            Click the tile to upload a new logo (PNG / JPG / SVG, max 5 MB) — logo changes save immediately.
          </p>
        </div>
        <FormField label="Name" required><Input value={editName} onChange={(e) => setEditName(e.target.value)} /></FormField>
        <FormField label="Domain"><Input value={editDomain} onChange={(e) => setEditDomain(e.target.value)} placeholder="acme.com" /></FormField>
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={() => setEditModal(false)} disabled={savingEdit}>Cancel</Button>
          <Button onClick={submitEdit} disabled={savingEdit || !editName.trim()}>{savingEdit ? 'Saving…' : 'Save'}</Button>
        </div>
      </Modal>

      <Modal open={planModal} onClose={() => setPlanModal(false)} title="Change plan">
        <FormField label="Plan"><Select value={newPlanId} onChange={(e) => setNewPlanId(e.target.value)}>
          {plans.filter(p => p.isActive).map(p => <option key={p._id} value={p._id}>{p.displayName}</option>)}
        </Select></FormField>
        <FormField label="Note (optional)"><Input value={planNote} onChange={(e) => setPlanNote(e.target.value)} /></FormField>
        <div className="flex justify-end gap-2"><Button variant="secondary" onClick={() => setPlanModal(false)}>Cancel</Button><Button onClick={submitChangePlan}>Save</Button></div>
      </Modal>

      <Modal open={extendModal} onClose={() => setExtendModal(false)} title="Extend expiry">
        <FormField label="New expiry"><Input type="date" value={newExpiry} onChange={(e) => setNewExpiry(e.target.value)} /></FormField>
        <FormField label="Note (optional)"><Input value={expiryNote} onChange={(e) => setExpiryNote(e.target.value)} /></FormField>
        <div className="flex justify-end gap-2"><Button variant="secondary" onClick={() => setExtendModal(false)}>Cancel</Button><Button onClick={submitExtend}>Save</Button></div>
      </Modal>

      <Modal open={seatsModal} onClose={() => setSeatsModal(false)} title="Edit module seats" widthClass="max-w-lg">
        <p className="text-xs text-text-muted mb-3">
          Set the number of seats this company gets per module. Pricing shown is per unit / month
          from the Pricing Catalog. Saving will enable custom pricing for this company.
        </p>
        <div className="grid grid-cols-2 gap-3">
          {SEAT_FIELDS.map((f) => {
            const price = unitPrice(f.key);
            return (
              <FormField
                key={f.key}
                label={f.label}
                error={price > 0 ? `${currency} ${price.toFixed(2)} / unit` : 'Price not set'}
              >
                <Input
                  type="number"
                  min={0}
                  value={seatsDraft[f.key] ?? ''}
                  onChange={(e) => {
                    const n = e.target.value === '' ? undefined : Math.max(0, Math.floor(Number(e.target.value) || 0));
                    setSeatsDraft((s) => ({ ...s, [f.key]: n }));
                  }}
                />
              </FormField>
            );
          })}
        </div>
        <div className="flex justify-end gap-2 mt-2">
          <Button variant="secondary" onClick={() => setSeatsModal(false)} disabled={savingSeats}>Cancel</Button>
          <Button onClick={submitSeats} disabled={savingSeats}>Save seats</Button>
        </div>
      </Modal>

      <ConfirmDialog open={confirmSuspend} title={c.status === 'suspended' ? 'Reactivate company?' : 'Suspend company?'}
        message={c.status === 'suspended' ? `${c.name} will be reactivated.` : `${c.name} users will be unable to log in.`}
        danger={c.status !== 'suspended'} onConfirm={toggleSuspend} onCancel={() => setConfirmSuspend(false)} />
    </>
  );
}
